import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowLeft, Gift, Search } from 'lucide-react';
import Header from '../../components/header/Header';
import Footer from '../../components/footer/Footer';
import LoginModal from '../../components/loginSection/LoginModal';
import ShareMenuButton from '../../components/share/ShareMenuButton';
import { clubsService, socialService } from '../../services';
import { useUser } from '../../UserContext';
import { getMembershipCapabilities } from '../../utils/membershipCapabilities';
import { getClubPublicPath } from '../../utils/clubPublicPaths';
import './ConnectPerks.css';

const filters = [
    { key: 'All', label: 'All' },
    { key: 'Public', label: 'Public' },
    { key: 'Members', label: 'Members' },
    { key: 'Premium', label: 'Premium' }
];

const formatDate = (value) => {
    if (!value) {
        return null;
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return null;
    }
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const getSessionId = () => {
    const storageKey = 'sc_session_id';
    try {
        const existing = localStorage.getItem(storageKey);
        if (existing) {
            return existing;
        }

        const generated = `sc-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
        localStorage.setItem(storageKey, generated);
        return generated;
    } catch (storageError) {
        return 'anonymous-session';
    }
};

const normalizeUrl = (url) => {
    if (!url) return null;
    return url.startsWith('http://') || url.startsWith('https://') ? url : `https://${url}`;
};

const ConnectPerks = () => {
    const { user } = useUser();
    const location = useLocation();
    const isDashboardRoute = location.pathname.startsWith('/dashboard');
    const showDashboardBackLink = isDashboardRoute && Boolean(location.state?.fromDashboard);
    const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
    const [audienceFilter, setAudienceFilter] = useState('Public');
    const [searchTerm, setSearchTerm] = useState('');
    const [offers, setOffers] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [showExpiringSoon, setShowExpiringSoon] = useState(false);
    const trackedOfferImpressionIdsRef = useRef(new Set());
    const membership = getMembershipCapabilities(user?.subscription || 'Free');

    const trackOfferEvent = useCallback(async (offer, eventType, eventLocation) => {
        if (!offer?.clubId || !offer?.clubOfferId) {
            return;
        }

        try {
            await clubsService.trackClubConversionEvent(offer.clubId, {
                eventType,
                clubOfferId: offer.clubOfferId,
                source: 'ConnectPerks',
                sessionId: getSessionId(),
                metadataJson: JSON.stringify({ location: eventLocation, audienceFilter })
            });
        } catch (trackError) {
            console.debug('Connect Perks tracking skipped:', trackError?.message || trackError);
        }
    }, [audienceFilter]);

    useEffect(() => {
        const fetchOffers = async () => {
            setIsLoading(true);
            try {
                const params = new URLSearchParams();
                params.append('take', '36');
                params.append('audience', audienceFilter);
                params.append('source', 'All');
                const data = await clubsService.getLatestOffers(params);
                setOffers(Array.isArray(data) ? data : []);
                trackedOfferImpressionIdsRef.current = new Set();
            } catch (error) {
                console.error('Failed to load connect perks:', error);
                setOffers([]);
            } finally {
                setIsLoading(false);
            }
        };

        fetchOffers();
    }, [audienceFilter]);

    const filteredOffers = useMemo(() => {
        const lowerSearch = searchTerm.trim().toLowerCase();
        return offers.filter((offer) => {
            const description = (offer.shortDescription || offer.fullDescription || '').toLowerCase();
            const title = (offer.title || '').toLowerCase();
            const sourceName = (offer.clubName || '').toLowerCase();
            const matchesSearch = !lowerSearch || title.includes(lowerSearch) || sourceName.includes(lowerSearch) || description.includes(lowerSearch);

            if (!showExpiringSoon) {
                return matchesSearch;
            }

            if (!offer.endDate) {
                return false;
            }

            const daysUntilEnd = Math.ceil((new Date(offer.endDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
            return matchesSearch && daysUntilEnd >= 0 && daysUntilEnd <= 7;
        });
    }, [offers, searchTerm, showExpiringSoon]);

    const sharePerk = async (offer, platform) => {
        try {
            const sharePlatform = platform === 'Copy' ? 'CopyLink' : platform;
            const data = await socialService.sharePerk({
                clubOfferId: offer.clubOfferId > 0 ? offer.clubOfferId : null,
                supplierPerkId: offer.supplierPerkId || null,
                offerKey: offer.offerKey
            }, sharePlatform);

            if (!data?.shareUrl) return;

            if (platform === 'Copy') {
                await navigator.clipboard.writeText(data.shareUrl);
                return;
            }

            window.open(data.shareUrl, '_blank', 'noopener,noreferrer,width=700,height=550');
        } catch (shareError) {
            console.error('Error sharing perk:', shareError);
        }
    };

    useEffect(() => {
        if (isLoading || !filteredOffers.length) {
            return;
        }

        const nextOffers = filteredOffers.filter((offer) => !trackedOfferImpressionIdsRef.current.has(offer.offerKey || offer.clubOfferId));
        if (!nextOffers.length) {
            return;
        }

        nextOffers.forEach((offer) => {
            trackedOfferImpressionIdsRef.current.add(offer.offerKey || offer.clubOfferId);
            trackOfferEvent(offer, 'PerksImpression', 'ConnectPerksList');
        });
    }, [filteredOffers, isLoading, trackOfferEvent]);

    return (
        <div className="connect-perks-page">
            {!isDashboardRoute && <Header openLoginModal={() => setIsLoginModalOpen(true)} />}

            <main className={`connect-perks-main ${isDashboardRoute ? 'dashboard-shell' : ''}`}>
                <div className="container">
                    {(!isDashboardRoute || showDashboardBackLink) && (
                        <Link className="connect-perks-back" to={isDashboardRoute ? '/dashboard/home' : '/'}>
                            <ArrowLeft size={16} />
                            {isDashboardRoute ? 'Back to dashboard' : 'Back home'}
                        </Link>
                    )}

                    <section className="connect-perks-hero">
                        <div>
                            <span className="connect-perks-pill">Connect Perks</span>
                            <h1>Find venue and supplier offers worth claiming</h1>
                            <p>
                                Browse live perks across Shoot Connect. Venue perks can be claimed in person with your Connect Pass QR, and supplier perks can be redeemed through official supplier links.
                            </p>
                        </div>
                    </section>

                    <section className="connect-perks-toolbar">
                        <div className="connect-perks-filter-group">
                            {filters.map((filter) => (
                                <button
                                    key={filter.key}
                                    type="button"
                                    className={audienceFilter === filter.key ? 'active' : ''}
                                    onClick={() => setAudienceFilter(filter.key)}
                                >
                                    {filter.label}
                                </button>
                            ))}
                        </div>
                        <div className="connect-perks-search">
                            <Search size={16} />
                            <input
                                type="text"
                                value={searchTerm}
                                onChange={(event) => setSearchTerm(event.target.value)}
                                placeholder="Search by source or perk"
                            />
                        </div>
                        <button
                            type="button"
                            className={`connect-perks-expiring ${showExpiringSoon ? 'active' : ''}`}
                            onClick={() => setShowExpiringSoon((current) => !current)}
                        >
                            Expiring soon
                        </button>
                    </section>

                    {isLoading ? (
                        <section className="connect-perks-state">
                            <Gift size={20} />
                            <p>Loading live Connect Perks...</p>
                        </section>
                    ) : audienceFilter === 'Premium' && !membership.canAccessPremiumPerks ? (
                        <section className="connect-perks-state">
                            <Gift size={20} />
                            <p>Premium perks require an active Premium entitlement.</p>
                            {user ? (
                                <Link className="connect-perk-cta" to="/dashboard/subscription">Manage membership</Link>
                            ) : (
                                <button className="connect-perk-cta" type="button" onClick={() => setIsLoginModalOpen(true)}>
                                    Sign in to continue
                                </button>
                            )}
                        </section>
                    ) : filteredOffers.length === 0 ? (
                        <section className="connect-perks-state">
                            <Gift size={20} />
                            <p>No perks match this filter right now.</p>
                        </section>
                    ) : (
                        <section className="connect-perks-grid">
                            {filteredOffers.map((offer) => (
                                <article key={offer.offerKey || offer.clubOfferId} className="connect-perk-card">
                                    <div className="connect-perk-card-top">
                                        <strong>{offer.title}</strong>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            {offer.isFeatured ? <span>Featured</span> : null}
                                            <ShareMenuButton onShare={(platform) => sharePerk(offer, platform)} buttonLabel="Share" />
                                        </div>
                                    </div>
                                    <p>{offer.shortDescription || offer.fullDescription || 'Live offer.'}</p>
                                    <div className="connect-perk-meta">
                                        <em>{offer.clubName}</em>
                                        <small>{offer.audience || 'Public'} · {offer.perkSource === 'Supplier' ? 'Supplier' : (offer.addedBy || 'Venue')}</small>
                                    </div>
                                    {offer.endDate ? (
                                        <div className="connect-perk-end">Ends {formatDate(offer.endDate)}</div>
                                    ) : null}
                                    {offer.perkSource === 'Supplier' ? (
                                        offer.ctaUrl ? (
                                            <a
                                                href={normalizeUrl(offer.ctaUrl)}
                                                className="connect-perk-cta"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                {offer.ctaLabel || 'View supplier offer'}
                                            </a>
                                        ) : (
                                            <span className="connect-perk-cta">Supplier offer managed by Shoot Connect</span>
                                        )
                                    ) : (
                                        <Link
                                            to={getClubPublicPath(offer)}
                                            className="connect-perk-cta"
                                            onClick={() => trackOfferEvent(offer, 'PerkClick', 'ConnectPerksCta')}
                                        >
                                            View venue and claim details
                                        </Link>
                                    )}
                                </article>
                            ))}
                        </section>
                    )}
                </div>
            </main>

            {!isDashboardRoute && <LoginModal isOpen={isLoginModalOpen} onClose={() => setIsLoginModalOpen(false)} />}
            {!isDashboardRoute && <Footer />}
        </div>
    );
};

export default ConnectPerks;
